/**
 * The call queue and scheduling (S06).
 *
 * Both are bulk operations driven by a multi-select, and both therefore report
 * what they skipped rather than failing outright. Selecting eleven contacts and
 * being told "one of them is on the do-not-call list, so none were queued" is a
 * dead end; queueing ten and naming the eleventh is not.
 */
import { z } from 'zod';

import { QueueStatus, Salutation, ScheduleStatus } from './enums.js';

/**
 * Just enough of a contact to render a queue row or a schedule row.
 *
 * Not the full `Contact`: that carries `lastOutcome`, `inQueue` and friends, each
 * of which costs a join. A queue panel needs a name and a number.
 */
export const contactSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  company: z.string().nullable(),
  phone: z.string(),
  salutation: Salutation.schema,
});
export type ContactSummary = z.infer<typeof contactSummarySchema>;

/** Why a contact in a multi-select was not acted on. */
export const SKIP_REASON = ['ALREADY_QUEUED', 'DO_NOT_CALL', 'NOT_FOUND'] as const;
export const skipReasonSchema = z.enum(SKIP_REASON);
export type SkipReason = z.infer<typeof skipReasonSchema>;

/** Messages the UI shows for a skip. Defined here so both sides agree. */
export const skipReasonMessages: Record<SkipReason, string> = {
  ALREADY_QUEUED: 'already in the queue',
  DO_NOT_CALL: 'on the do-not-call list',
  NOT_FOUND: 'no longer exists',
};

export const skippedSchema = z.object({
  contactId: z.string().uuid(),
  /** Carried so a toast can name the person rather than a uuid. */
  name: z.string().nullable(),
  reason: skipReasonSchema,
});
export type Skipped = z.infer<typeof skippedSchema>;

// ---------------------------------------------------------------------------
// Queue
// ---------------------------------------------------------------------------

export const queueEntrySchema = z.object({
  id: z.string().uuid(),
  contact: contactSummarySchema,
  /** Null is "next available" — the wording the approved mock uses. */
  targetTime: z.string().datetime().nullable(),
  position: z.number().int(),
  status: QueueStatus.schema,
  /** A DIALING entry whose contact has picked up — "Call in progress". */
  inCall: z.boolean(),
  createdAt: z.string().datetime(),
});
export type QueueEntry = z.infer<typeof queueEntrySchema>;

/**
 * The queue, plus its count.
 *
 * The count is returned rather than derived from `items.length` because the
 * sidebar badge (S06: "the queue persists and drives the sidebar badge") is
 * rendered by the shell, which has no business paging through the queue to
 * discover a number.
 */
export const queueListSchema = z.object({
  items: z.array(queueEntrySchema),
  count: z.number().int().min(0),
  /**
   * Calls ringing or connected right now, from any origin.
   *
   * Counted from calls rather than from queue entries, because an instant call
   * has no queue entry and is still very much in progress — a count off the
   * queue would say "nothing is happening" while somebody was mid-conversation.
   *
   * It rides along on this response rather than having an endpoint of its own so
   * that the contacts page, which already polls this, can show live state
   * without a second request.
   */
  callsInFlight: z.number().int().min(0),
});
export type QueueList = z.infer<typeof queueListSchema>;

export const addToQueueSchema = z.object({
  contactIds: z.array(z.string().uuid()).min(1).max(500),
  /** Omitted or null means "next available". */
  targetTime: z.string().datetime().nullable().optional(),
});
export type AddToQueue = z.infer<typeof addToQueueSchema>;

export const bulkQueueResultSchema = z.object({
  added: z.number().int().min(0),
  skipped: z.array(skippedSchema),
});
export type BulkQueueResult = z.infer<typeof bulkQueueResultSchema>;

// ---------------------------------------------------------------------------
// Scheduling
// ---------------------------------------------------------------------------

export const scheduledCallSchema = z.object({
  id: z.string().uuid(),
  contact: contactSummarySchema,
  scheduledFor: z.string().datetime(),
  /** The zone the operator scheduled in, so a DST shift stays reproducible. */
  timezone: z.string(),
  status: ScheduleStatus.schema,
  createdAt: z.string().datetime(),
});
export type ScheduledCall = z.infer<typeof scheduledCallSchema>;

export const scheduleCallsSchema = z.object({
  contactIds: z.array(z.string().uuid()).min(1).max(500),
  scheduledFor: z.string().datetime(),
  /**
   * IANA zone. Defaults to the operator's business zone rather than the server's,
   * which is a machine detail and could differ per deployment (Q10 confirmed
   * Europe/Sofia, uniform for v1).
   */
  timezone: z.string().min(1).max(60).default('Europe/Sofia'),
});
export type ScheduleCalls = z.infer<typeof scheduleCallsSchema>;

export const bulkScheduleResultSchema = z.object({
  scheduled: z.number().int().min(0),
  skipped: z.array(skippedSchema),
});
export type BulkScheduleResult = z.infer<typeof bulkScheduleResultSchema>;

export const scheduledCallListSchema = z.object({
  items: z.array(scheduledCallSchema),
  count: z.number().int().min(0),
});
export type ScheduledCallList = z.infer<typeof scheduledCallListSchema>;
